import { useState } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { useAuth } from '../../contexts/AuthContext';
import GeneralInfo from '../NewReport/GeneralInfo';
import AttendanceSection from '../NewReport/AttendanceSection';
import { WorshipReport } from '../../types/worshipReport';
import { calculateTotalParticipants, calculateNewMembersTotal } from '../../utils/calculations';
import { toast } from 'sonner';

interface WorshipReportFormProps {
  onSubmit?: (data: WorshipReport) => void;
  onClose?: () => void;
}

export default function WorshipReportForm({ onSubmit, onClose }: WorshipReportFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<WorshipReport>>({
    cellName: "Vases d'Honneur Angre Djibi 3",
    sourceChurch: "Eglise Vases D'honneur Centre Kodesh",
    date: new Date().toISOString().split('T')[0],
    speakerId: '',
    messageTheme: '',
    messageSource: '',
    attendance: {
      adults: { men: 0, women: 0 },
      children: { boys: 0, girls: 0 },
      served: { men: 0, women: 0 },
      blooms: 0,
      conversions: { men: 0, women: 0 }
    },
    newMembers: { men: 0, women: 0, blooms: 0, children: 0 },
    notes: '',
    needsNotes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.attendance || !formData.newMembers || !formData.speakerId) {
        throw new Error('Missing required fields');
      }

      const totalParticipants = calculateTotalParticipants(formData);
      const totalNewMembers = calculateNewMembersTotal(formData.newMembers);
      
      const reportData = {
        ...formData,
        totalParticipants,
        totalNewMembers,
        createdAt: new Date(),
        createdBy: user?.id
      } as WorshipReport;

      const docRef = await addDoc(collection(db, 'worshipReports'), reportData);
      
      // Sauvegarder les besoins si présents
      if (formData.needsNotes) {
        await saveNeedsFromNotes({
          reportId: docRef.id,
          reportType: 'worship',
          department: 'Gestion de Culte',
          needsNotes: formData.needsNotes,
          reportDate: formData.date!
        });
      }
      
      if (onSubmit) {
        onSubmit({ ...reportData, id: docRef.id });
      }

      toast.success('Rapport de culte enregistré avec succès');
    } catch (error) {
      console.error('Error saving worship report:', error);
      toast.error('Erreur lors de l\'enregistrement du rapport de culte');
    }
  };

  const handleChange = (_section: keyof WorshipReport, data: any) => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <GeneralInfo onChange={handleChange as any} initialData={formData} />
      <AttendanceSection onChange={(data) => handleChange('attendance', data)} />
      
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Notes / Observations
        </label>
        <textarea
          placeholder="Observations particulières..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          onChange={(e) => handleChange('notes', { notes: e.target.value })}
        />
      </div>

      {/* Besoins */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Liste des besoins
        </label>
        <textarea
          placeholder="Listez les besoins du département (un besoin par ligne)..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          value={formData.needsNotes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
        />
        <p className="mt-1 text-xs text-gray-500">
          Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
        </p>
      </div>

      <div className="flex justify-end space-x-4">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annuler
          </button>
        )}
        <button
          type="submit"
          className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
        >
          Enregistrer le rapport de culte
        </button>
      </div>
    </form>
  );
}